import {
  useTable,
  UseSortByColumnProps,
  ColumnInstance,
  Row,
  useSortBy,
  TableInstance,
} from 'react-table'
import {KTCard, KTIcon, KTCardBody, useDebounce} from '../../../../_metronic/helpers'
import {Loader} from '../../product-master/loader'
import {CustomHeaderColumn, CustomRow, styles} from './Utils'
import {useState, useMemo, useCallback, useEffect, useRef} from 'react'
import {useLayout} from '../../../../_metronic/layout/core'
import {getViewTransactionColumns} from './Columns'
import {Transaction, deleteTransaction, getTransactionsList} from '../API/ViewTransactionsAPI'
import {toast} from 'react-hot-toast'
import * as XLSX from 'xlsx'
import ViewTransactionModal from './ViewTransactionModal'
import DeleteTransactionModal from './DeleteTransactionModal'

type SortableColumn = ColumnInstance<Transaction> & UseSortByColumnProps<Transaction>
type TableInstanceWithHooks = TableInstance<Transaction> & {
  headers: Array<ColumnInstance<Transaction> & UseSortByColumnProps<Transaction>>
}

export const ViewTransactionsTable: React.FC<{activeTab: string}> = ({activeTab}) => {
  const {company, financialYear} = useLayout()
  const tableRef: any = useRef(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<any>({
    transactions: [],
    ledgers: {},
    // openingBalance: 0,
    // closingBalance: 0,
    // debitAmount: 0,
    // creditAmount: 0,
  })
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showViewTransactionModal, setShowViewTransactionModal] = useState(false)
  const [transactionData, setTransactionData]: any = useState(null)
  // console.log(data)
  const columns = useMemo(
    () =>
      getViewTransactionColumns(
        activeTab,
        showDeleteModal,
        setShowDeleteModal,
        showViewTransactionModal,
        setShowViewTransactionModal,
        setTransactionData,
        data.ledgers
      ),
    [activeTab, showDeleteModal, showViewTransactionModal, data.ledgers]
  )

  const debouncedSearchTerm = useDebounce(searchTerm, 150)

  const filteredData: Transaction[] = useMemo(() => {
    if (!data || !data.transactions) return []

    if (debouncedSearchTerm?.length) {
      return data.transactions.filter((transaction: any) => {
        const transactionMatches =
          transaction?.voucherName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          transaction?.voucherNumber
            ?.toString()
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase()) ||
          transaction?.narration?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())

        const transactionItemMatches = transaction.transactionItems.some((item: any) => {
          const ledgerInfo = data.ledgers[item.ledgerId]
          return (
            ledgerInfo?.parentLedger?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            ledgerInfo?.subLedger?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            item.transactionAmount
              .toString()
              .toLowerCase()
              .includes(debouncedSearchTerm.toLowerCase())
          )
        })

        return transactionMatches || transactionItemMatches
      })
    }
    return data.transactions
  }, [debouncedSearchTerm, data.transactions.length])

  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable(
    {
      columns,
      data: debouncedSearchTerm?.length ? filteredData : data?.transactions || [],
    },
    useSortBy
  ) as TableInstanceWithHooks

  const getAllGroups = useCallback(async () => {
    if (company && company.value && financialYear && financialYear.value && !!activeTab) {
      try {
        setIsError(false)
        setIsLoading(true)
        const res = await getTransactionsList(company?.value, financialYear.value, activeTab)
        // console.log(res)
        res && setData(res)
      } catch (err) {
        setIsError(true)
      } finally {
        setIsLoading(false)
      }
    }
  }, [company, financialYear, activeTab])

  const handleDelete = async function () {
    // console.log(transactionData)
    if (company?.value && financialYear?.value)
      await deleteTransaction(transactionData.transactionId)
        .then((res) => {
          res?.detail && toast.success(res.detail)
          if (showViewTransactionModal) setShowViewTransactionModal(false)
        })
        .catch((err) => err && toast.error(err))
        .finally(() => {
          setShowDeleteModal(false)
          setShowViewTransactionModal(false)
        })
  }

  useEffect(() => {
    !showDeleteModal && getAllGroups()
  }, [getAllGroups, showDeleteModal])
  // console.log(transactionData)

  useEffect(() => {
    if (!showViewTransactionModal) setTransactionData(null)
  }, [showViewTransactionModal])

  return (
    <>
      {transactionData && (
        <ViewTransactionModal
          showViewTransactionModal={showViewTransactionModal}
          setShowViewTransactionModal={setShowViewTransactionModal}
          ledgerData={data?.ledgers || {}}
          transactionData={transactionData}
          handleDelete={handleDelete}
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
          key={Date.now()}
        />
      )}

      <DeleteTransactionModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        handleDelete={handleDelete}
      />

      <KTCard>
        {!isLoading && (
          <>
            <div className='card-header border-0 pt-6'>
              <div className='card-title'>
                {/* begin::Search */}
                <div className='d-flex align-items-center position-relative my-1'>
                  <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                  <input
                    type='text'
                    data-kt-user-table-filter='search'
                    className='form-control form-control-solid w-250px ps-14'
                    placeholder='Search'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                {/* end::Search */}
              </div>
              {/* begin::Card toolbar */}
              <div className='card-toolbar'>
                <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
                  <button
                    type='button'
                    className='btn p-0'
                    style={{
                      backgroundColor: 'var(--bs-primary)',
                      color: 'var(--bs-primary-inverse)',
                      display: 'inline-flex',
                      height: '43.59px',
                      alignItems: 'center',
                      justifyContent: 'space-between',

                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    }}
                    disabled={!data?.transactions?.length}
                    onClick={() => {
                      toast.success('Download has been started successfully')
                      const ws = XLSX.utils.table_to_sheet(tableRef.current)

                      // Add additional data to the worksheet if needed

                      const wb = XLSX.utils.book_new()
                      XLSX.utils.book_append_sheet(wb, ws, 'ViewTransactions')

                      // Save the file
                      const fileName = `ViewTransaction_${company.label}_${financialYear.value}.xlsx`
                      XLSX.writeFile(wb, fileName)
                    }}
                  >
                    <span className='px-5'>Download</span>
                    <span
                      style={{
                        color: '#fff',
                        backgroundColor: '#006060',
                        padding: '0 15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        borderTopRightRadius: 'inherit',
                        borderBottomRightRadius: 'inherit',
                      }}
                    >
                      <KTIcon iconName='arrow-down' className='fs-2 text-white' />
                    </span>
                  </button>
                </div>
              </div>
              {/* end::Card toolbar */}
            </div>
            <KTCardBody className='py-4'>
              <div className='table-responsive'>
                <table
                  ref={tableRef}
                  id='view_transactions_table'
                  className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
                  {...getTableProps()}
                >
                  <thead>
                    <tr className='text-start text-muted fw-bolder bg-light ps-4 fs-7 text-uppercase gs-0'>
                      {headers.map((column: SortableColumn) => (
                        <CustomHeaderColumn key={column.id} column={column} />
                      ))}
                    </tr>
                  </thead>
                  <tbody className='text-gray-600 fw-bold' {...getTableBodyProps()}>
                    {rows.length > 0 ? (
                      rows.map((row: Row<any>, i) => {
                        prepareRow(row)

                        return (
                          <CustomRow row={row} key={`row-${i}-${row.id}`} ledgers={data.ledgers} />
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                            No matching records found
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </KTCardBody>
          </>
        )}
        {isLoading && <Loader isError={isError} />}
      </KTCard>
    </>
  )
}
